import { useEffect, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import clsx from 'clsx';

import 'react-calendar/dist/Calendar.css';

import { FormControl } from '@@/form-components/FormControl';
import { Button, ButtonGroup } from '@@/buttons';

import { TimeColumns } from './TimeColumns';
import styles from './DateTimeRangePicker.module.css';

type Value = { start: Date; end: Date | null };
type Endpoint = 'start' | 'end';

const DISPLAY_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
};

export function DateTimeRangePicker({
  value,
  onChange,
  name,
  label = 'Date range',
  minDate,
}: {
  value: Value | undefined;
  onChange: (value?: Value) => void;
  name?: string;
  /** Set to null to render the picker without a FormControl label/wrapper. */
  label?: string | null;
  minDate?: Date;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeEndpoint, setActiveEndpoint] = useState<Endpoint>('start');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const picker = (
    <div className="relative" ref={containerRef}>
      <div className="form-control flex w-auto items-center gap-2 border th-highcontrast:border-white">
        <button
          type="button"
          name={name}
          onClick={() => setIsOpen((open) => !open)}
          className="flex-1 border-none bg-transparent text-left text-sm"
        >
          {formatValue(value)}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            aria-label="Clear date range"
            className="border-none"
          >
            <X size={16} />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Open date range picker"
          className="border-none"
        >
          <CalendarIcon size={16} />
        </button>
      </div>

      {isOpen && (
        <div
          style={{ top: 'calc(100% + 8px)' }}
          className="absolute left-0 z-10 flex rounded-md border border-gray-4 bg-white shadow-lg th-highcontrast:border-white th-highcontrast:bg-black th-dark:border-gray-8 th-dark:bg-gray-11"
        >
          <Calendar
            className={clsx(
              styles.calendar,
              'w-auto rounded-md p-2 font-[inherit]'
            )}
            tileClassName={styles.tile}
            selectRange
            allowPartialRange
            minDate={minDate}
            value={value ? [value.start, value.end] : null}
            onChange={handleCalendarChange}
          />
          <div className="flex flex-col gap-2 border-l border-gray-4 p-2 th-highcontrast:border-white th-dark:border-gray-8">
            <ButtonGroup size="xsmall">
              <Button
                color={activeEndpoint === 'start' ? 'secondary' : 'default'}
                onClick={() => setActiveEndpoint('start')}
                data-cy="datetime-range-picker-start-tab"
                className="flex-1"
              >
                Start
              </Button>
              <Button
                color={activeEndpoint === 'end' ? 'secondary' : 'default'}
                onClick={() => setActiveEndpoint('end')}
                disabled={!value?.end}
                data-cy="datetime-range-picker-end-tab"
                className="flex-1"
              >
                End
              </Button>
            </ButtonGroup>
            <TimeColumns value={activeTime()} onChange={handleTimeChange} />
          </div>
        </div>
      )}
    </div>
  );

  if (label === null) {
    return picker;
  }

  return <FormControl label={label}>{picker}</FormControl>;

  function activeTime(): Date {
    if (activeEndpoint === 'end' && value?.end) {
      return value.end;
    }
    return value?.start ?? new Date();
  }

  function handleTimeChange(date: Date) {
    if (!value) {
      return;
    }
    if (activeEndpoint === 'end' && value.end) {
      onChange({ start: value.start, end: date });
      return;
    }
    onChange({ start: date, end: value.end });
  }

  function handleCalendarChange(nextValue: unknown) {
    if (!Array.isArray(nextValue)) {
      return;
    }
    const [start, end] = nextValue as [Date | null, Date | null];
    if (!start) {
      onChange(undefined);
      return;
    }
    setActiveEndpoint('start');
    onChange({ start, end: end ?? null });
  }
}

function formatValue(value: Value | undefined): string {
  if (!value) {
    return 'Select date range';
  }
  const start = new Intl.DateTimeFormat(undefined, DISPLAY_FORMAT).format(
    value.start
  );
  if (!value.end) {
    return start;
  }
  const end = new Intl.DateTimeFormat(undefined, DISPLAY_FORMAT).format(
    value.end
  );
  return `${start} – ${end}`;
}
